import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import VitrinaTVPlayer from "core/vitrinaTV/VitrinaTVPlayer";
import getBrowserTimezone from "core/vitrinaTV/getBrowserTimezone";
import useLoadVitrinaSdk from "core/vitrinaTV/useLoadVitrinaSdk";
import { ChannelEpgItemsQuery } from "core/queries";
import useRegisterOccupiedZone from "core/hooks/useRegisterOccupiedZone";
import LocalStorage from "core/utils/localstorage";
import PlayerPanel from "core/components/PlayerPanel";
import PlayerFooterButton from "core/components/PlayerFooterButton";
import QualityButton from "core/components/QualityButton/QualityButton";
import SubtitlesButton from "core/components/SubtitlesButton/SubtitlesButton";
import ChannelSchedule from "core/components/ChannelSchedule";
import LiveButton from "core/components/LiveButton";
import FullscreenButton from "core/components/FullscreenButton";
import BigPlayButton from "core/components/BigPlayButton";
import SkuflixPlayIcon from "core/icons/skuflix/SkuflixPlayIcon";
import SkuflixPauseIcon from "core/icons/skuflix/SkuflixPauseIcon";
import SkuflixVolumeOffIcon from "core/icons/skuflix/SkuflixVolumeOffIcon";
import SkuflixVolumeUpIcon from "core/icons/skuflix/SkuflixVolumeUpIcon";
import Slider from "@mui/material/Slider";
import { createEvent } from "core/utils/tracker";

const Root = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  backgroundColor: theme.palette.mode === "dark" ? "#000" : "rgba(0,0,0,0.9)",
  overflow: "hidden",
}));

const PlayerContainer = styled("div")({
  position: "absolute",
  inset: 0,
});

const ControlsRoot = styled("div")(({ theme }) => ({
  display: "flex",
  width: "auto",
  position: "absolute",
  minHeight: 56,
  alignItems: "center",
  left: theme.spacing(1),
  right: theme.spacing(1),
  "@media (max-width: 450px)": {
    left: theme.spacing(0.5),
    right: theme.spacing(0.5),
  },
  [theme.breakpoints.up("sm")]: {
    left: theme.spacing(2),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up("md")]: {
    left: theme.spacing(3),
    right: theme.spacing(3),
  },
}));

const LeftControls = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const RightControls = styled("div")(({ theme }) => ({
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const ProgrammeLabel = styled(Typography)({
  color: "#fff",
  maxWidth: "40vw",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "0.875rem",
});

const VolumeSlider = styled(Slider)({
  marginRight: 10,
  width: 50,
  "@media (max-width: 450px)": {
    display: "none",
  },
  "& .MuiSlider-track": {
    backgroundColor: "#fff",
    border: 0,
  },
  "& .MuiSlider-thumb": {
    backgroundColor: "#fff",
  },
  "& .MuiSlider-rail": {
    backgroundColor: "#fff",
  },
});

const LoaderOverlay = styled("div")({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  zIndex: 4,
  backgroundColor: "rgba(0, 0, 0, 0.25)",
});

const CHANNEL_PLAYER_UNSUPPORTED_CODES = new Set([
  4032,
  "CONTENT_UNSUPPORTED_BY_BROWSER",
  "CONTENT_IS_NOT_AVAILABLE_BY_BROWSER_DRM_COMPATIBILITY",
  "WATERMARK_REMOVED",
]);
const CHANNEL_PLAYER_GEO_RESTRICTED_CODES = new Set([
  451,
  "CONTENT_IS_NOT_AVAILABLE_IN_COUNTRY",
]);

const CHANNEL_PLAYER_GEO_RESTRICTED_PATTERNS = [
  "geo",
  "geoblock",
  "geo_block",
  "geo_restricted",
  "georestricted",
  "country",
  "region",
  "territory",
  "territorial",
  "not available in your country",
  "not allowed in your country",
];

const CHANNEL_PLAYER_DOMAIN_RESTRICTED_PATTERNS = [
  "invalid host",
  "invalid_host",
  "invalid host name",
  "invalid hostname",
  "invalid domain",
  "host is not allowed",
  "hostname is not allowed",
  "domain is not allowed",
  "origin is not allowed",
  "referrer is not allowed",
  "forbidden host",
  "forbidden domain",
  "wrong domain",
];

const CHANNEL_PLAYER_BROWSER_RESTRICTED_PATTERNS = [
  "unsupported",
  "drm",
  "codec",
  "encrypted",
  "eme",
  "media key",
  "widevine",
  "watermark_removed",
  "content_unsupported_by_browser",
  "content_is_not_available_by_browser_drm_compatibility",
];

const CHANNEL_PLAYER_STREAM_RESTRICTED_PATTERNS = [
  "stream",
  "offline",
  "playlist",
  "manifest",
  "source",
  "channel unavailable",
  "no channel",
  "not found",
  "timeout",
  "network",
];
const CHANNEL_PLAYER_READY_STATES = new Set([
  "ready",
  "playing",
  "paused",
  "ended",
]);
const CHANNEL_PLAYER_SILENT_WINDOW_LOG_EVENTS = new Set([
  "timeupdate",
  "mousemove",
  "mouseleave",
]);
const channelPlayerStorage = new LocalStorage("video-player");

// settime "now" попадает в текущий ~30-секундный чанк и не двигает плеер
// к краю прямого эфира. Для Live плеер принимает unixtime, поэтому отправляем
// время заведомо в будущем — плеер сам ограничит до актуального live-края.
const LIVE_EDGE_LOOKAHEAD_SECONDS = 60;

function getChannelPlayerErrorCode(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const directCode =
    payload.code ?? payload.errorCode ?? payload?.error?.code ?? null;

  if (directCode === null || directCode === undefined) {
    return null;
  }

  const numericCode = Number(directCode);

  if (Number.isNaN(numericCode)) {
    return directCode;
  }

  return numericCode;
}

function getChannelPlayerErrorMessage(payload) {
  if (!payload) {
    return null;
  }

  if (typeof payload === "string") {
    return payload;
  }

  if (payload instanceof Error) {
    return payload.message || payload.name || null;
  }

  if (typeof payload === "object") {
    return (
      payload.message ||
      payload.error ||
      payload.reason ||
      payload.description ||
      payload.detail ||
      payload?.details?.error ||
      payload?.error?.message ||
      payload?.details?.message ||
      null
    );
  }

  return null;
}

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

function includesAnyPattern(value, patterns) {
  return patterns.some((pattern) => value.includes(pattern));
}

function getChannelPlayerErrorFingerprint({ payload, code, error }) {
  const normalizedCode =
    code === null || code === undefined ? "" : String(code).toLowerCase();
  const normalizedError = String(error || "").toLowerCase();
  const normalizedPayload = safeStringify(payload).toLowerCase();

  return [normalizedCode, normalizedError, normalizedPayload]
    .filter(Boolean)
    .join(" ");
}

function isGeoRestrictedIssue({ code, fingerprint }) {
  if (CHANNEL_PLAYER_GEO_RESTRICTED_CODES.has(code)) {
    return true;
  }

  return includesAnyPattern(
    fingerprint,
    CHANNEL_PLAYER_GEO_RESTRICTED_PATTERNS
  );
}

function isDomainRestrictedIssue({ fingerprint }) {
  return includesAnyPattern(
    fingerprint,
    CHANNEL_PLAYER_DOMAIN_RESTRICTED_PATTERNS
  );
}

function isBrowserCompatibilityIssue({ code, fingerprint }) {
  if (CHANNEL_PLAYER_UNSUPPORTED_CODES.has(code)) {
    return true;
  }

  return includesAnyPattern(
    fingerprint,
    CHANNEL_PLAYER_BROWSER_RESTRICTED_PATTERNS
  );
}

function isNoStreamIssue({ fingerprint }) {
  return includesAnyPattern(
    fingerprint,
    CHANNEL_PLAYER_STREAM_RESTRICTED_PATTERNS
  );
}

function normalizePlayerStateValue(value) {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue || null;
}

function extractPlayerState(payload) {
  if (typeof payload === "string") {
    return normalizePlayerStateValue(payload);
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const directState = normalizePlayerStateValue(
    payload.state ||
      payload.status ||
      payload.playerState ||
      payload.playerStatus ||
      payload.phase
  );

  if (directState) {
    return directState;
  }

  if (payload.state && typeof payload.state === "object") {
    const nestedState = normalizePlayerStateValue(
      payload.state?.state ||
        payload.state?.status ||
        payload.state?.name ||
        payload.state?.value
    );

    if (nestedState) {
      return nestedState;
    }
  }

  const nestedPayload = payload.data || payload.payload || payload.eventData;
  if (nestedPayload && nestedPayload !== payload) {
    return extractPlayerState(nestedPayload);
  }

  return null;
}

function toFiniteNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
}

function normalizeVolumeValue(value, fallback = 1) {
  const parsedValue = toFiniteNumber(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.max(0, Math.min(1, parsedValue));
}

function getStoredVolume() {
  return normalizeVolumeValue(channelPlayerStorage.getItem("volume"), 1);
}

function getStoredMuted() {
  return Boolean(channelPlayerStorage.getItem("muted"));
}

function extractSdkFullscreenState(payload) {
  if (typeof payload === "boolean") {
    return payload;
  }

  if (typeof payload === "number") {
    if (payload === 1) {
      return true;
    }
    if (payload === 0) {
      return false;
    }
  }

  if (typeof payload === "string") {
    const normalizedValue = payload.trim().toLowerCase();
    if (
      ["fullscreen", "enter", "entered", "on", "true", "1"].includes(
        normalizedValue
      )
    ) {
      return true;
    }
    if (
      ["normal", "windowed", "exit", "exited", "off", "false", "0"].includes(
        normalizedValue
      )
    ) {
      return false;
    }
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  const directState =
    payload.isFullscreen ??
    payload.is_fullscreen ??
    payload.fullscreen ??
    payload.fullScreen;

  if (directState !== undefined && directState !== null) {
    return extractSdkFullscreenState(directState);
  }

  const nestedPayload =
    payload.data || payload.payload || payload.state || payload.eventData;
  if (nestedPayload && nestedPayload !== payload) {
    return extractSdkFullscreenState(nestedPayload);
  }

  return null;
}

function isErrorLikeState(payload) {
  const state = extractPlayerState(payload);
  if (!state) {
    return false;
  }

  return state === "error" || state === "failed" || state === "fatal";
}

function hasErrorShape(payload) {
  if (!payload || typeof payload !== "object") {
    return false;
  }

  return Boolean(
    payload.error ||
      payload.reason ||
      payload.code ||
      payload.errorCode ||
      payload?.details?.error ||
      payload?.details?.message ||
      payload?.error?.message
  );
}

function isErrorLikeSdkEvent(eventName, payload) {
  const normalizedEventName = String(eventName || "").toLowerCase();
  if (normalizedEventName.includes("error")) {
    return true;
  }

  if (normalizedEventName === "statechange" && isErrorLikeState(payload)) {
    return true;
  }

  return hasErrorShape(payload);
}

function extractAdvertsMode(payload) {
  if (typeof payload === "boolean") {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (typeof payload.adverts === "boolean") {
    return payload.adverts;
  }

  if (typeof payload.isAdvertsMode === "boolean") {
    return payload.isAdvertsMode;
  }

  if (typeof payload.adMode === "boolean") {
    return payload.adMode;
  }

  if (typeof payload.advertising === "boolean") {
    return payload.advertising;
  }

  if (typeof payload.env?.adverts === "boolean") {
    return payload.env.adverts;
  }

  if (typeof payload.env?.isAdvertsMode === "boolean") {
    return payload.env.isAdvertsMode;
  }

  return null;
}

function logChannelPlayerDiagnostics(label, payload) {
  const consoleRef = typeof window !== "undefined" ? window.console : null;
  if (!consoleRef) {
    return;
  }

  const logger =
    consoleRef.error || consoleRef.warn || consoleRef.log || (() => undefined);

  logger.call(consoleRef, `[ChannelPlayer] ${label}`, payload);
}

function parseMessageData(rawData) {
  if (!rawData) {
    return null;
  }

  if (typeof rawData === "string") {
    try {
      return JSON.parse(rawData);
    } catch {
      return null;
    }
  }

  if (typeof rawData === "object") {
    return rawData;
  }

  return null;
}

function normalizeChannelPlayerError(payload, source) {
  const code = getChannelPlayerErrorCode(payload);
  const error = getChannelPlayerErrorMessage(payload);
  const fatal = Boolean(
    payload?.fatal ?? payload?.isFatal ?? payload?.error?.fatal
  );
  const fingerprint = getChannelPlayerErrorFingerprint({
    payload,
    code,
    error,
  });

  let type = "generic";
  if (isGeoRestrictedIssue({ code, fingerprint })) {
    type = "invalid_country";
  } else if (isDomainRestrictedIssue({ fingerprint })) {
    type = "invalid_host";
  } else if (isBrowserCompatibilityIssue({ code, fingerprint })) {
    type = "browser_drm_compatibility";
  } else if (isNoStreamIssue({ fingerprint })) {
    type = "no_stream";
  }

  return {
    source,
    code,
    fatal,
    error,
    payload,
    type,
  };
}

function normalizeProgramme(programme) {
  if (!programme || typeof programme !== "object") {
    return null;
  }

  const titleFromLocales =
    Array.isArray(programme.titles) && programme.titles.length > 0
      ? programme.titles[0]?.title
      : null;
  const descriptionFromLocales =
    Array.isArray(programme.descriptions) && programme.descriptions.length > 0
      ? programme.descriptions[0]?.description
      : null;

  return {
    id:
      programme.id ||
      programme.programmeId ||
      programme.vitrinatv_programme_id ||
      `${programme.title || "programme"}-${
        programme.dateFrom || programme.start || ""
      }`,
    title: programme.title || titleFromLocales || programme.name || "",
    dateFrom:
      programme.dateFrom ||
      programme.start ||
      programme.startTime ||
      programme.start_dt_iso8601_unrounded ||
      programme.start_dt_iso8601,
    dateTo:
      programme.dateTo ||
      programme.end ||
      programme.endTime ||
      programme.stop_dt_iso8601_unrounded ||
      programme.stop_dt_iso8601,
    description:
      programme.description ||
      descriptionFromLocales ||
      programme.synopsis ||
      "",
  };
}

function findCurrentProgrammeFromItems(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const now = Date.now();

  return (
    items.find((item) => {
      const start = getDateTimestamp(item?.dateFrom);
      const end = getDateTimestamp(item?.dateTo);

      return start !== null && end !== null && now >= start && now < end;
    }) || null
  );
}

function getDateTimestamp(value) {
  if (!value) {
    return null;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function getQualityValue(qualityItem) {
  if (!qualityItem) {
    return null;
  }

  if (typeof qualityItem === "string") {
    return qualityItem;
  }

  return qualityItem.id || qualityItem.label || qualityItem.name || null;
}

// SDK может присылать значение качества "auto"/"abr" в общем списке.
// Свой пункт "Авто" (ABR) добавляет QualityButton, поэтому SDK-значение
// трактуем как ABR (quality === null), а из списка треков исключаем.
function isAutoQualityValue(value) {
  if (typeof value !== "string") {
    return false;
  }

  return ["auto", "abr", "авто"].includes(value.trim().toLowerCase());
}

function normalizeCaptions(payload) {
  const items = Array.isArray(payload)
    ? payload
    : payload?.captions || payload?.tracks || payload?.items || [];

  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((caption, index) => {
      if (typeof caption === "string") {
        return {
          track: caption,
          label: caption,
        };
      }

      if (!caption || typeof caption !== "object") {
        return null;
      }

      const track =
        caption.track ||
        caption.id ||
        caption.language ||
        caption.lang ||
        caption.code ||
        null;
      const fallbackLabel = track || `caption-${index + 1}`;
      const label = caption.label || caption.title || fallbackLabel;

      if (!track) {
        return null;
      }

      return {
        track,
        label,
      };
    })
    .filter(Boolean);
}

let channelPlayerContainerIdCounter = 0;
const TRACK_DURATION_HEARTBEAT_MS = 5 * 1000;
const CONTROLS_IDLE_TIMEOUT_MS = 5 * 1000;
const TOUCH_TO_CLICK_DEDUP_MS = 700;

export default function ChannelPlayer({
  contentId,
  configUrl,
  vlight,
  fullscreenTarget = "player",
  autoPlay,
  muted: initialMuted,
  hideTitle,
  onCurrentProgrammeChange,
  onVideoStateChange,
  onError,
  viewDataTracker,
}) {
  const { t } = useTranslation("core");
  const containerIdRef = useRef(null);
  if (!containerIdRef.current) {
    channelPlayerContainerIdCounter += 1;
    containerIdRef.current = `vitrina-tv-${channelPlayerContainerIdCounter}`;
  }
  const containerId = containerIdRef.current;
  const playerRef = useRef(null);
  const footerRef = useRegisterOccupiedZone("footer", true);
  const controlsHideTimeoutRef = useRef(null);

  const autoPlayRef = useRef(autoPlay);
  autoPlayRef.current = autoPlay;
  const initialMutedValue =
    typeof initialMuted === "boolean" ? initialMuted : getStoredMuted();
  const initialVolumeValue = getStoredVolume();

  const onCurrentProgrammeChangeRef = useRef(onCurrentProgrammeChange);
  onCurrentProgrammeChangeRef.current = onCurrentProgrammeChange;

  const onVideoStateChangeRef = useRef(onVideoStateChange);
  onVideoStateChangeRef.current = onVideoStateChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Держим актуальные videoActions в ref, чтобы эффект не зависел от нестабильных функций
  const latestVideoActionsRef = useRef(null);

  const [status, setStatus] = useState(autoPlay ? "playing" : "paused");
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsVisibleRef = useRef(controlsVisible);
  controlsVisibleRef.current = controlsVisible;
  const ignoreSyntheticClickUntilRef = useRef(0);
  const [muted, setMuted] = useState(initialMutedValue);
  const [volume, setVolume] = useState(initialVolumeValue);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  const [qualities, setQualities] = useState([]);
  const [quality, setQuality] = useState(null);
  const [captions, setCaptions] = useState([]);
  const [selectedCaption, setSelectedCaption] = useState(null);
  const [sdkCurrentProgramme, setSdkCurrentProgramme] = useState(null);
  const [epgAnchor, setEpgAnchor] = useState(null);
  const [epgDirection, setEpgDirection] = useState("AROUND_NOW");
  const [needUserGesture, setNeedUserGesture] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlayerStateReady, setIsPlayerStateReady] = useState(false);
  const [isSdkFullscreen, setIsSdkFullscreen] = useState(false);
  const [advertsMode, setAdvertsMode] = useState(false);
  const statusRef = useRef(status);
  statusRef.current = status;
  const advertsModeRef = useRef(advertsMode);
  advertsModeRef.current = advertsMode;
  const shouldLoadSdk = Boolean(configUrl && vlight);
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const { isLoaded: isSdkLoaded, error: sdkLoadError } = useLoadVitrinaSdk({
    enabled: shouldLoadSdk,
  });

  const { data: epgData, loading: epgLoading } = useQuery(
    ChannelEpgItemsQuery,
    {
      variables: {
        contentId,
        timezone: browserTimezone,
        anchor: epgAnchor,
        direction: epgDirection,
        limit: 4,
      },
      skip: !contentId,
      pollInterval: epgDirection === "AROUND_NOW" ? 60000 : 0,
    }
  );

  const epgPage = epgData?.channelEpgItems;
  const epgItems = epgPage?.items || [];
  const graphqlCurrentProgramme = useMemo(
    () => findCurrentProgrammeFromItems(epgItems),
    [epgItems]
  );
  const currentProgramme = graphqlCurrentProgramme || sdkCurrentProgramme;

  useEffect(() => {
    setEpgAnchor(null);
    setEpgDirection("AROUND_NOW");
    setSdkCurrentProgramme(null);
  }, [contentId]);

  useEffect(() => {
    onCurrentProgrammeChangeRef.current?.(currentProgramme);
  }, [currentProgramme]);

  const handleEpgPrevious = useCallback(() => {
    const firstItem = epgItems[0];
    if (!firstItem?.dateFrom) {
      return;
    }

    setEpgDirection("PAST");
    setEpgAnchor(firstItem.dateFrom);
  }, [epgItems]);

  const handleEpgNext = useCallback(() => {
    const lastItem = epgItems[epgItems.length - 1];
    if (!lastItem?.dateTo) {
      return;
    }

    setEpgDirection("FUTURE");
    setEpgAnchor(lastItem.dateTo);
  }, [epgItems]);

  const sendMessage = useCallback((name, data) => {
    playerRef.current?.sendMessage(name, data);
  }, []);

  const handleCurrentProgrammeUpdate = useCallback((payload) => {
    const programme = normalizeProgramme(
      payload?.programme || payload?.currentProgramme || payload
    );

    if (!programme) {
      return;
    }

    setSdkCurrentProgramme(programme);
  }, []);

  const togglePlayByVideoClick = useCallback(() => {
    if (advertsModeRef.current) {
      return;
    }

    const playingNow = statusRef.current === "playing";
    if (playingNow) {
      sendMessage("pause");
      setStatus("paused");
      createEvent("channel", "pause");
      return;
    }

    setPlayerError(null);
    onErrorRef.current?.(null);
    sendMessage("play");
    setStatus("playing");
    setNeedUserGesture(false);
    createEvent("channel", "play");
  }, [sendMessage]);

  const clearControlsHideTimeout = useCallback(() => {
    if (controlsHideTimeoutRef.current) {
      clearTimeout(controlsHideTimeoutRef.current);
      controlsHideTimeoutRef.current = null;
    }
  }, []);

  const scheduleControlsHide = useCallback(() => {
    clearControlsHideTimeout();

    if (statusRef.current !== "playing") {
      return;
    }

    controlsHideTimeoutRef.current = setTimeout(() => {
      if (statusRef.current === "playing") {
        setControlsVisible(false);
      }
    }, CONTROLS_IDLE_TIMEOUT_MS);
  }, [clearControlsHideTimeout]);

  const markControlsActivity = useCallback(() => {
    setControlsVisible(true);
    scheduleControlsHide();
  }, [scheduleControlsHide]);

  const handlePlayerSurfaceInteraction = useCallback(
    (interactionSource) => {
      const now = Date.now();
      const isTouchEndInteraction = interactionSource === "touchend";
      const isSyntheticClickAfterTouch =
        interactionSource === "click" &&
        now <= ignoreSyntheticClickUntilRef.current;

      if (isSyntheticClickAfterTouch) {
        return;
      }

      if (isTouchEndInteraction) {
        ignoreSyntheticClickUntilRef.current = now + TOUCH_TO_CLICK_DEDUP_MS;
      } else if (interactionSource === "click") {
        ignoreSyntheticClickUntilRef.current = 0;
      }

      const shouldTogglePlay = controlsVisibleRef.current;

      markControlsActivity();

      // Align with regular video/stream UX:
      // first tap/click while controls are hidden only reveals controls.
      if (!shouldTogglePlay) {
        return;
      }

      togglePlayByVideoClick();
    },
    [markControlsActivity, togglePlayByVideoClick]
  );

  const playedIntervalsRef = useRef([]);
  const openedIntervalRef = useRef(null);

  const buildTrackedIntervals = useCallback(({ includeOpenInterval }) => {
    const now = new Date();
    const intervals = playedIntervalsRef.current.map(({ from, to }) => ({
      from,
      to,
    }));

    if (includeOpenInterval && openedIntervalRef.current) {
      intervals.push({
        from: openedIntervalRef.current,
        to: now,
      });
    }

    return intervals.filter(({ from, to }) => {
      return Boolean(from) && Boolean(to) && to.getTime() > from.getTime();
    });
  }, []);

  const openTrackInterval = useCallback(() => {
    if (openedIntervalRef.current) {
      return;
    }

    openedIntervalRef.current = new Date();
  }, []);

  const closeTrackInterval = useCallback(() => {
    if (!openedIntervalRef.current) {
      return false;
    }

    const from = openedIntervalRef.current;
    const to = new Date();
    openedIntervalRef.current = null;

    if (to.getTime() <= from.getTime()) {
      return false;
    }

    playedIntervalsRef.current = playedIntervalsRef.current.concat({
      from,
      to,
    });

    return true;
  }, []);

  const trackDurationDebounce = useCallback(() => {
    const timeIntervals = buildTrackedIntervals({ includeOpenInterval: true });

    if (!timeIntervals.length) {
      return;
    }

    viewDataTracker.trackDebounce({
      trackDuration: {
        timeIntervals,
      },
    });
  }, [buildTrackedIntervals, viewDataTracker]);

  const reportChannelPlayerError = useCallback((payload, source) => {
    const normalizedError = normalizeChannelPlayerError(payload, source);

    setPlayerError(normalizedError);
    setStatus("paused");
    logChannelPlayerDiagnostics("caught error", {
      source,
      normalizedError,
      rawPayload: payload,
    });

    onErrorRef.current?.(normalizedError);
  }, []);

  useEffect(() => {
    if (
      !configUrl ||
      !vlight ||
      !isSdkLoaded ||
      typeof document === "undefined"
    ) {
      return undefined;
    }

    const player = new VitrinaTVPlayer(containerId, configUrl, vlight, {
      useControls: false,
    });

    setIsPlayerStateReady(false);
    playerRef.current = player;

    const unsubscribers = [
      player.on("playerinitend", () => {
        setPlayerError(null);
        onErrorRef.current?.(null);
        setPlayerReady(true);
        setAdvertsMode(false);
        const shouldMute = Boolean(mutedRef.current);
        player.sendMessage("setmuted", shouldMute);
        player.sendMessage("setvolume", shouldMute ? 0 : volumeRef.current);
        player.sendMessage("getcurrentprogramme");
        player.sendMessage("getdvrwindow");
        player.sendMessage("gettime");

        if (autoPlayRef.current) {
          player.sendMessage("play");
        }
      }),
      player.on("statechange", (payload) => {
        const nextStatus = extractPlayerState(payload);
        const nextNeedUserGesture =
          payload?.env?.needUserGesture ?? payload?.needUserGesture;
        const stateChangeLooksLikeError = isErrorLikeState(payload);
        logChannelPlayerDiagnostics("sdk statechange", {
          payload,
          state: nextStatus,
          needUserGesture: nextNeedUserGesture,
        });

        if (stateChangeLooksLikeError) {
          reportChannelPlayerError(payload, "sdk-event-statechange-error");
          return;
        }

        if (nextStatus === "playing" || nextStatus === "paused") {
          setStatus(nextStatus);
        }

        if (CHANNEL_PLAYER_READY_STATES.has(nextStatus)) {
          setPlayerReady(true);
          setIsPlayerStateReady(true);
        }

        if (nextStatus === "ended") {
          setStatus("paused");
        }

        if (typeof nextNeedUserGesture === "boolean") {
          setNeedUserGesture(nextNeedUserGesture);
        }
      }),
      player.on("envstate", (payload) => {
        const nextNeedUserGesture = payload?.needUserGesture;
        if (typeof nextNeedUserGesture === "boolean") {
          setNeedUserGesture(nextNeedUserGesture);
        }
      }),
      player.on("advertsmodechange", (payload) => {
        const nextAdvertsMode = extractAdvertsMode(payload);
        if (typeof nextAdvertsMode === "boolean") {
          setAdvertsMode(nextAdvertsMode);
        }
      }),
      player.on("volumechange", (payload) => {
        const nextVolume = normalizeVolumeValue(payload?.volume, null);
        const nextMuted =
          typeof payload?.muted === "boolean" ? payload.muted : null;

        if (Number.isFinite(nextVolume)) {
          setVolume(nextVolume);
          channelPlayerStorage.setItem("volume", nextVolume);
        }

        if (typeof nextMuted === "boolean") {
          setMuted(nextMuted);
          channelPlayerStorage.setItem("muted", nextMuted);
        }
      }),
      player.on("fullscreenstate", (payload) => {
        const nextFullscreenState = extractSdkFullscreenState(payload);
        if (typeof nextFullscreenState === "boolean") {
          setIsSdkFullscreen(nextFullscreenState);
        }
      }),
      player.on("click", () => {
        handlePlayerSurfaceInteraction("click");
      }),
      player.on("touchend", () => {
        handlePlayerSurfaceInteraction("touchend");
      }),
      player.on("mousemove", () => {
        markControlsActivity();
      }),
      player.on("currentprogramme", handleCurrentProgrammeUpdate),
      player.on("programmechange", handleCurrentProgrammeUpdate),
      player.on("availablequalities", (payload) => {
        const nextQualities = Array.isArray(payload)
          ? payload
          : payload?.qualities || payload?.availableQualities || [];

        if (Array.isArray(nextQualities)) {
          setQualities(nextQualities);
        }
      }),
      player.on("qualitychange", (payload) => {
        const nextQuality =
          getQualityValue(payload) ||
          getQualityValue(payload?.quality) ||
          getQualityValue(payload?.currentQuality) ||
          (typeof payload === "string" ? payload : null);

        if (nextQuality) {
          setQuality(nextQuality);
        }
      }),
      player.on("availablecaptions", (payload) => {
        setCaptions(normalizeCaptions(payload));
      }),
      player.on("captionschange", (payload) => {
        if (payload === false || payload === "disabled") {
          setSelectedCaption(null);
          return;
        }

        const nextCaption =
          (typeof payload === "string" ? payload : null) ||
          payload?.track ||
          payload?.language ||
          payload?.lang ||
          null;

        setSelectedCaption(nextCaption);
      }),
      player.on("error", (payload) => {
        reportChannelPlayerError(payload, "sdk-event-error");
      }),
      player.on("__all__", ({ event, payload }) => {
        if (!isErrorLikeSdkEvent(event, payload)) {
          return;
        }

        logChannelPlayerDiagnostics("sdk error-like event", {
          event,
          payload,
        });
      }),
      player.on("needusergesture", () => {
        setNeedUserGesture(true);
        setStatus("paused");
      }),
    ];

    player.createPlayer();

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      player.destroy();
      playerRef.current = null;
      setPlayerReady(false);
      setIsPlayerStateReady(false);
      setPlayerError(null);
      setNeedUserGesture(false);
      setIsSdkFullscreen(false);
      setAdvertsMode(false);
      setQualities([]);
      setQuality(null);
      setCaptions([]);
      setSelectedCaption(null);
      setSdkCurrentProgramme(null);
      clearControlsHideTimeout();
    };
  }, [
    configUrl,
    // containerId is stable for component lifetime, intentionally in deps
    containerId,
    vlight,
    isSdkLoaded,
    handleCurrentProgrammeUpdate,
    reportChannelPlayerError,
    handlePlayerSurfaceInteraction,
    markControlsActivity,
    clearControlsHideTimeout,
  ]);

  useEffect(() => {
    if (!sdkLoadError) {
      return;
    }

    reportChannelPlayerError(sdkLoadError, "sdk-load");
  }, [sdkLoadError, reportChannelPlayerError]);

  useEffect(() => {
    if (playerError || advertsMode) {
      clearControlsHideTimeout();
      return;
    }

    if (status === "playing") {
      setControlsVisible(true);
      scheduleControlsHide();
      return;
    }

    clearControlsHideTimeout();
    setControlsVisible(true);
  }, [
    status,
    playerError,
    advertsMode,
    scheduleControlsHide,
    clearControlsHideTimeout,
  ]);

  useEffect(() => {
    return () => {
      clearControlsHideTimeout();
    };
  }, [clearControlsHideTimeout]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const onWindowMessage = (event) => {
      const parsedData = parseMessageData(event.data);
      const eventName =
        parsedData?.event || parsedData?.type || parsedData?.name || null;

      if (!eventName) {
        return;
      }

      const knownSdkEvents = new Set([
        "statechange",
        "advertsmodechange",
        "timeupdate",
        "durationchange",
        "volumechange",
        "progress",
        "bufferingchange",
        "seekallowedchange",
        "qualitychange",
        "availablequalities",
        "captionschange",
        "availablecaptions",
        "error",
        "fullscreenstate",
        "envstate",
        "dvravailabilitychange",
        "dvrwindowchange",
        "dvrpositionchange",
        "iteminfo",
        "keypress",
        "playerinitstart",
        "playerinitend",
        "click",
        "touchend",
        "timezonechange",
        "currentprogramme",
        "programmechange",
        "allprogrammes",
        "getallprogrammes",
        "needusergesture",
        "сontextmenucall",
        "castavailable",
        "caststart",
        "castend",
      ]);

      if (
        !parsedData?.eump_remote_message &&
        !knownSdkEvents.has(String(eventName).toLowerCase())
      ) {
        return;
      }

      const normalizedEventName = String(eventName).toLowerCase();
      const messagePayload =
        parsedData?.data || parsedData?.payload || parsedData || {};

      if (
        normalizedEventName === "mousemove" ||
        normalizedEventName === "touchend" ||
        normalizedEventName === "click"
      ) {
        markControlsActivity();
      }

      if (normalizedEventName === "statechange") {
        const nextStatus = extractPlayerState(messagePayload);
        const nextNeedUserGesture =
          messagePayload?.env?.needUserGesture ??
          messagePayload?.needUserGesture;

        if (CHANNEL_PLAYER_READY_STATES.has(nextStatus)) {
          setPlayerReady(true);
          setIsPlayerStateReady(true);
        }

        if (nextStatus === "playing" || nextStatus === "paused") {
          setStatus(nextStatus);
        }

        if (typeof nextNeedUserGesture === "boolean") {
          setNeedUserGesture(nextNeedUserGesture);
        }
      }

      if (normalizedEventName === "fullscreenstate") {
        const nextFullscreenState = extractSdkFullscreenState(messagePayload);
        if (typeof nextFullscreenState === "boolean") {
          setIsSdkFullscreen(nextFullscreenState);
        }
      }

      if (CHANNEL_PLAYER_SILENT_WINDOW_LOG_EVENTS.has(normalizedEventName)) {
        return;
      }

      logChannelPlayerDiagnostics("window message", {
        event: eventName,
        payload: messagePayload,
        origin: event.origin,
        rawData: event.data,
      });
    };

    window.addEventListener("message", onWindowMessage);

    return () => {
      window.removeEventListener("message", onWindowMessage);
    };
  }, [markControlsActivity, clearControlsHideTimeout]);

  useEffect(() => {
    if (!playerReady) {
      return;
    }

    sendMessage("getdvrwindow");
    sendMessage("gettime");
    sendMessage("getquality");
    sendMessage("getavailablequalities");
    sendMessage("getavailablecaptions");
  }, [playerReady, sendMessage]);

  useEffect(() => {
    if (status === "playing") {
      openTrackInterval();
      trackDurationDebounce();
      return;
    }

    if (closeTrackInterval()) {
      trackDurationDebounce();
    }
  }, [status, openTrackInterval, closeTrackInterval, trackDurationDebounce]);

  useEffect(() => {
    if (status !== "playing") {
      return undefined;
    }

    const heartbeatId = setInterval(
      () => trackDurationDebounce(),
      TRACK_DURATION_HEARTBEAT_MS
    );

    return () => {
      clearInterval(heartbeatId);
    };
  }, [status, trackDurationDebounce]);

  useEffect(() => {
    return () => {
      closeTrackInterval();

      const timeIntervals = buildTrackedIntervals({
        includeOpenInterval: false,
      });

      if (!timeIntervals.length) {
        return;
      }

      viewDataTracker.track({
        trackDuration: {
          timeIntervals,
        },
      });
    };
  }, [buildTrackedIntervals, closeTrackInterval, viewDataTracker]);

  const canRenderControls =
    playerReady && isPlayerStateReady && !playerError && !advertsMode;
  const visible = !playerError && !advertsMode && controlsVisible;
  const showPlayerLoader =
    shouldLoadSdk &&
    !playerError &&
    (!isSdkLoaded || !playerReady || !isPlayerStateReady);
  const showCenterPlayButton = canRenderControls && status === "paused";

  const onClickPlay = () => {
    setPlayerError(null);
    onErrorRef.current?.(null);
    sendMessage("play");
    setStatus("playing");
    setNeedUserGesture(false);
    createEvent("channel", "play");
  };

  const onClickPause = () => {
    sendMessage("pause");
    setStatus("paused");
    createEvent("channel", "pause");
  };

  const onSeekToLive = useCallback(() => {
    setPlayerError(null);
    onErrorRef.current?.(null);
    const futureUnixTime =
      Math.floor(Date.now() / 1000) + LIVE_EDGE_LOOKAHEAD_SECONDS;
    sendMessage("settime", futureUnixTime);
    sendMessage("play");
    setStatus("playing");
    setNeedUserGesture(false);
    createEvent("channel", "play");
  }, [sendMessage]);

  const onToggleSdkFullscreen = useCallback(() => {
    if (isSdkFullscreen) {
      sendMessage("exitfullscreen");
      setIsSdkFullscreen(false);
      return;
    }

    sendMessage("requestfullscreen");
    setIsSdkFullscreen(true);
  }, [isSdkFullscreen, sendMessage]);

  const onToggleMute = () => {
    const nextMuted = !muted;
    setMuted(nextMuted);
    sendMessage("setmuted", nextMuted);
    channelPlayerStorage.setItem("muted", nextMuted);
    if (!nextMuted) {
      const restoreVolume = volume > 0 ? volume : 1;
      setVolume(restoreVolume);
      sendMessage("setvolume", restoreVolume);
      channelPlayerStorage.setItem("volume", restoreVolume);
    }
  };

  // Синхронизируем ref с актуальными videoActions при каждом рендере
  latestVideoActionsRef.current = {
    toggleMute: onToggleMute,
    play: () => sendMessage("play"),
    pause: () => sendMessage("pause"),
    togglePlayUser: () => {
      if (status === "playing") {
        sendMessage("pause");
      } else {
        sendMessage("play");
      }
    },
  };

  useEffect(() => {
    if (!onVideoStateChangeRef.current || !playerReady) {
      return;
    }

    const isUserPause = status === "paused" && !needUserGesture;

    onVideoStateChangeRef.current(
      {
        status,
        isUserPause,
        isStarted: status === "playing",
        rawProgramDateTime: currentProgramme?.dateFrom ?? null,
        muted,
        paused: status !== "playing",
      },
      latestVideoActionsRef.current
    );
  }, [status, muted, needUserGesture, currentProgramme, playerReady]);

  const onChangeVolume = (event, value) => {
    const normalizedValue = normalizeVolumeValue(value, 1);
    const nextMuted = normalizedValue === 0;

    setVolume(normalizedValue);
    setMuted(nextMuted);
    sendMessage("setvolume", normalizedValue);
    sendMessage("setmuted", nextMuted);
    channelPlayerStorage.setItem("volume", normalizedValue);
    channelPlayerStorage.setItem("muted", nextMuted);
  };

  const channelFooterVideoState = useMemo(() => {
    const tracks = qualities
      .map((item, index) => {
        const sdkValue = getQualityValue(item);
        if (!sdkValue || isAutoQualityValue(sdkValue)) {
          return null;
        }

        return {
          id: String(index),
          name: sdkValue,
          width: sdkValue,
          active: quality === sdkValue,
          sdkValue,
        };
      })
      .filter(Boolean);

    const textTracks = captions.map(({ track, label }) => ({
      language: track,
      label,
      mode: selectedCaption === track ? "showing" : "disabled",
    }));

    return {
      tracks,
      selectedTrack:
        tracks.find((track) => track.active) ||
        tracks.find((track) => track.sdkValue === quality) ||
        null,
      abrEnabled: quality === null,
      textTracks,
    };
  }, [captions, qualities, quality, selectedCaption]);

  const channelFooterVideoActions = useMemo(
    () => ({
      selectVariantTrack: (track) => {
        if (!track) {
          sendMessage("setquality");
          setQuality(null);
          return;
        }

        sendMessage("setquality", track.sdkValue);
        setQuality(track.sdkValue);
      },
      setTextTrack: (trackLanguage) => {
        if (trackLanguage === "disabled" || !trackLanguage) {
          sendMessage("setcaptions", false);
          setSelectedCaption(null);
          return;
        }

        sendMessage("setcaptions", trackLanguage);
        setSelectedCaption(trackLanguage);
      },
    }),
    [sendMessage]
  );

  const channelLiveVideoState = useMemo(
    () => ({
      live: true,
      isUserPause: status === "paused" && !needUserGesture,
    }),
    [status, needUserGesture]
  );
  const channelLiveVideoActions = useMemo(
    () => ({
      seekToLive: onSeekToLive,
    }),
    [onSeekToLive]
  );
  // Каналы вещаются только в прямом эфире (без DVR/таймшифта),
  // поэтому offset всегда 0: LiveButton работает как индикатор «в эфире»,
  // а после паузы позволяет вернуться к прямому эфиру.
  const channelLiveSeekBar = useMemo(
    () => ({
      offset: 0,
      setOffset: () => {
        onSeekToLive();
      },
    }),
    [onSeekToLive]
  );

  const VolumeIcon = muted ? SkuflixVolumeOffIcon : SkuflixVolumeUpIcon;

  return (
    <Root data-testid="ChannelPlayer">
      <PlayerContainer id={containerId} />

      {showPlayerLoader && (
        <LoaderOverlay>
          <CircularProgress size={38} color="inherit" />
        </LoaderOverlay>
      )}

      {showCenterPlayButton && (
        <BigPlayButton
          onClick={onClickPlay}
          videoState={{ isLoading: showPlayerLoader }}
        />
      )}

      {canRenderControls && (
        <PlayerPanel visible={visible} position="absolute" disableGutters>
          <ControlsRoot
            ref={footerRef}
            onMouseMove={markControlsActivity}
            onMouseEnter={markControlsActivity}
            onTouchStart={markControlsActivity}
          >
            <LeftControls>
              {status === "playing" ? (
                <PlayerFooterButton title={t("pause")} onClick={onClickPause}>
                  <SkuflixPauseIcon />
                </PlayerFooterButton>
              ) : (
                <PlayerFooterButton title={t("play")} onClick={onClickPlay}>
                  <SkuflixPlayIcon />
                </PlayerFooterButton>
              )}

              <LiveButton
                videoState={channelLiveVideoState}
                videoActions={channelLiveVideoActions}
                liveSeekBar={channelLiveSeekBar}
              />

              {!hideTitle && currentProgramme?.title && (
                <ProgrammeLabel title={currentProgramme.title}>
                  {currentProgramme.title}
                </ProgrammeLabel>
              )}

              <ChannelSchedule
                items={epgItems}
                loading={epgLoading}
                hasPrevious={Boolean(epgPage?.hasPrevious)}
                hasNext={Boolean(epgPage?.hasNext)}
                onPrevious={handleEpgPrevious}
                onNext={handleEpgNext}
                timezone={epgPage?.timezone}
                channelVersionName={epgPage?.channelVersionName}
              />
            </LeftControls>

            <RightControls>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <VolumeSlider
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={onChangeVolume}
                  aria-label={t("player.volume")}
                />
                <PlayerFooterButton
                  title={muted ? t("unmute") : t("mute")}
                  onClick={onToggleMute}
                >
                  <VolumeIcon />
                </PlayerFooterButton>
              </Box>

              <SubtitlesButton
                videoState={channelFooterVideoState}
                videoActions={channelFooterVideoActions}
              />
              <QualityButton
                videoState={channelFooterVideoState}
                videoActions={channelFooterVideoActions}
              />

              <FullscreenButton
                target={fullscreenTarget}
                content={{ id: contentId }}
                customFullscreen={{
                  enabled: true,
                  isFullscreen: isSdkFullscreen,
                  toggle: onToggleSdkFullscreen,
                }}
              />
            </RightControls>
          </ControlsRoot>
        </PlayerPanel>
      )}
    </Root>
  );
}

ChannelPlayer.propTypes = {
  contentId: PropTypes.string.isRequired,
  configUrl: PropTypes.string.isRequired,
  vlight: PropTypes.string.isRequired,
  fullscreenTarget: PropTypes.string,
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,
  hideTitle: PropTypes.bool,
  onCurrentProgrammeChange: PropTypes.func,
  onVideoStateChange: PropTypes.func,
  onError: PropTypes.func,
  viewDataTracker: PropTypes.object.isRequired,
};
